import { useCallback, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router';

import { commitOwnerKey, discardGuestKey, generateGuestKey, unwrapBridgeKey } from '@/crypto';
import { transport } from '@/api/transport';
import { ApiError, type NearbyEvent } from '@/api/types';
import { bridgePath } from '@/lib/links';
import { sasEmoji, type Sas } from '@/lib/nearby';
import { admitsOffer, expectsReveal, step, type GuestEffect, type GuestInput, type Incoming } from '@/lib/nearbyGuest';
import { setClaimToken } from '@/lib/storage';
import { useLatest } from '@/lib/useLatest';

export interface IncomingOffer {
  readonly incoming: Incoming | null;
  /** The offer events of the stream addressed to this device as the guest; the token is the page's current one. */
  readonly onEvent: (event: NearbyEvent, token: string | null) => void;
  readonly onTokenChanged: () => void;
  readonly accept: () => void;
  readonly decline: () => void;
}

interface Run {
  readonly token: string | null;
  readonly navigate: NavigateFunction;
  readonly dispatch: (input: GuestInput) => void;
}

/** Carries out one effect; what comes of it goes back in as an input. */
function perform(effect: GuestEffect, { token, navigate, dispatch }: Run): void {
  switch (effect.type) {
    case 'discard':
      discardGuestKey(effect.pair);
      return;
    case 'decline':
      if (token !== null) void transport.nearbyDecline({ token, offerId: effect.offerId }).catch(() => undefined);
      return;
    case 'answer':
      if (token === null) return;
      transport.nearbyAnswer({ token, offerId: effect.offerId, guestPublicKey: effect.guestPublicKey }).catch(() => {
        dispatch({ type: 'answerFailed', offerId: effect.offerId });
      });
      return;
    case 'accept':
      if (token === null) return;
      transport.nearbyAccept({ token, offerId: effect.offerId }).catch((error: unknown) => {
        dispatch({ type: 'acceptFailed', offerId: effect.offerId, reason: error instanceof ApiError && error.code === 'not_found' ? 'expired' : 'cancelled' });
      });
      return;
    case 'unwrap': {
      const { incoming: shown, ready } = effect;
      if (shown.ownerPublicKey === null) return;
      unwrapBridgeKey(shown.pair, shown.ownerPublicKey, ready.wrappedKey, { flow: 'nearby', id: ready.offerId }).then(
        (key) => {
          setClaimToken(ready.bridgeId, ready.claimToken);
          dispatch({ type: 'unwrapped', offerId: ready.offerId });
          void navigate(bridgePath(ready.bridgeId, key));
        },
        () => {
          dispatch({ type: 'unwrapFailed', offerId: ready.offerId });
        },
      );
    }
  }
}

/**
 * Runs the receiving side of Nearby (`lib/nearbyGuest.ts`): feeds it what the stream and the person do, and carries
 * out the effects it asks for. The state also lives in a ref, so two events in a row see each other's result.
 */
export function useIncomingOffer(tokenRef: { readonly current: string | null }, navigate: NavigateFunction): IncomingOffer {
  const [incoming, setIncoming] = useState<Incoming | null>(null);
  const current = useRef<Incoming | null>(null);

  const navigation = useLatest(navigate);
  // one function for the life of the provider, so effects can feed their outcomes back through it
  const [dispatch] = useState(() => {
    const feed = (input: GuestInput): void => {
      const next = step(current.current, input);
      current.current = next.state;
      setIncoming(next.state === null ? null : { ...next.state });
      for (const effect of next.effects) perform(effect, { token: tokenRef.current, navigate: navigation.current, dispatch: feed });
    };
    return feed;
  });

  const onOffer = useCallback(
    async (event: Extract<NearbyEvent, { type: 'offer' }>): Promise<void> => {
      // a busy screen, or an offer that is not ours, needs no key pair: it is refused before one is made
      if (!admitsOffer(current.current, event)) {
        dispatch({ type: 'refuse', offerId: event.offerId });
        return;
      }
      const pair = await generateGuestKey();
      dispatch({ type: 'offer', offer: event, pair });
    },
    [dispatch],
  );

  const onReveal = useCallback(
    async (event: Extract<NearbyEvent, { type: 'reveal' }>): Promise<void> => {
      const shown = current.current;
      if (!expectsReveal(shown, event.offerId)) return;
      let emoji: readonly Sas[] | null = null;
      try {
        if ((await commitOwnerKey('nearby', event.ownerPublicKey)) === shown.ownerKeyHash) emoji = await sasEmoji('nearby', event.ownerPublicKey, shown.pair.publicKey);
      } catch {
        emoji = null;
      }
      dispatch({ type: 'revealed', offerId: event.offerId, ownerPublicKey: event.ownerPublicKey, emoji });
    },
    [dispatch],
  );

  const onEvent = useCallback(
    (event: NearbyEvent, token: string | null): void => {
      if (token === null) return;
      switch (event.type) {
        case 'offer':
          void onOffer(event);
          return;
        case 'reveal':
          void onReveal(event);
          return;
        case 'ready':
          dispatch({ type: 'ready', ready: event });
          return;
        case 'closed':
          dispatch({ type: 'closed', offerId: event.offerId, reason: event.reason });
          return;
        default:
      }
    },
    [dispatch, onOffer, onReveal],
  );

  const onTokenChanged = useCallback(() => {
    dispatch({ type: 'tokenChanged' });
  }, [dispatch]);
  const accept = useCallback(() => {
    dispatch({ type: 'accept' });
  }, [dispatch]);
  const decline = useCallback(() => {
    dispatch({ type: 'decline' });
  }, [dispatch]);

  return { incoming, onEvent, onTokenChanged, accept, decline };
}
